<template>
  <v-dialog
    v-model="dialog"
    width="auto"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-form ref="form" v-model="valid" @submit.prevent="submit()">
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <alert-message></alert-message>
            <v-row>
              <v-col cols="12" sm="1" md="1" lg="1">
                <color-select v-model="form.task.color" />
              </v-col>
              <v-col cols="10" sm="11" md="11" lg="11">
                <task-combo
                  v-model="src"
                  label="Job Name *"
                  :solo="false"
                  @change="handleChangeName"
                  @blur="$v.form.taskId.$touch()"
                  return-object
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="6">
                <v-row>
                  <v-col cols="6">
                    <date-picker
                      :disabled="isDatetimeDisabled"
                      v-model="form.task.tempStartDate"
                      label="Start Date *"
                      :error-messages="
                        [].concat(fieldErrors('form.task.tempStartDate'))
                      "
                      @blur="$v.form.task.tempStartDate.$touch()"
                      :hide-details="true"
                    />
                  </v-col>
                  <v-col cols="6">
                    <time-picker
                      :disabled="isDatetimeDisabled"
                      v-model="form.task.tempStartTime"
                      label="Time *"
                      :error-messages="
                        [].concat(fieldErrors('form.task.tempStartTime'))
                      "
                      @blur="$v.form.task.tempStartTime.$touch()"
                    />
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" sm="12" md="6">
                <v-row>
                  <v-col cols="6">
                    <date-picker
                      :disabled="isDatetimeDisabled"
                      v-model="form.task.tempEndDate"
                      label="End Date *"
                      :error-messages="
                        [].concat(fieldErrors('form.task.tempEndDate'))
                      "
                      @blur="$v.form.task.tempEndDate.$touch()"
                      :hide-details="true"
                    />
                  </v-col>
                  <v-col cols="6">
                    <time-picker
                      :disabled="isDatetimeDisabled"
                      v-model="form.task.tempEndTime"
                      label="Time *"
                      :error-messages="
                        [].concat(fieldErrors('form.task.tempEndTime'))
                      "
                      @blur="$v.form.task.tempEndTime.$touch()"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="12" md="12">
                <client-select
                  :error-messages="[].concat(fieldErrors('form.task.clientId'))"
                  v-model="form.task.clientId"
                  label="Select Client *"
                  @blur="$v.form.task.clientId.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-textarea
                  v-model="form.task.description"
                  label="Description"
                  :error-messages="
                    [].concat(fieldErrors('form.task.description'))
                  "
                  @blur="$v.form.task.description.$touch()"
                  rows="3"
                  auto-grow
                ></v-textarea>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <user-select
                  v-model="form.userIds"
                  label="Assign To *"
                  :error-messages="[].concat(fieldErrors('form.userIds'))"
                  @blur="$v.form.userIds.$touch()"
                  :multiple="true"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn
                  @click="viewJobDetail()"
                  v-if="item && item.id"
                  x-small
                  class="ml-2"
                  >Job Details</v-btn
                >
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-show="isEdit"
            color="error"
            :loading="loadingDelete"
            @click="deleteSchedule"
            >Delete</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="darken-1" @click="dialog = false">Cancel</v-btn>
          <v-btn
            :disabled="$v.$invalid"
            type="submit"
            color="info"
            :loading="loading"
          >
            {{ isEdit ? "Save" : "Add" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <AttachedFileListDialog
      :show="showAttachFile"
      :item="item"
      object="task"
      @onClosed="clearAttachFile()"
      @onSucceded="refresh()"
    />

    <TaskForm
      :show="showJobDetail"
      :item="jobDetail"
      object="task"
      @onClosed="clearJobDetail()"
      @onSucceded="refreshAndClose()"
    />
  </v-dialog>
</template>

<script>
import UTIL from "src/helpers/utility";
import { mapActions, mapState } from "vuex";
import AlertMessage from "src/components/AlertMessage";
import { required } from "vuelidate/lib/validators";
import validationMixin from "@/mixins/validationMixin";
import ClientSelect from "src/components/ClientSelect.vue";
import DatePicker from "src/components/DatePicker.vue";
import UserSelect from "src/components/UserSelect";
import formatterMixin from "@/mixins/formatterMixin";
import TimePicker from "components/TimePickerTimestamp.vue";
import ColorSelect from "components/ColorSelect";
import TaskCombo from "components/TaskSelect";
import { userService } from "src/services/api";
import AttachedFileListDialog from "components/FilesAttachment/TaskAttachedFileListDialog";
import TaskForm from "./_FormJob";

const defaultInputs = {
  task: {
    id: "",
    code: "",
    name: "",
    status: "open",
    type: "Labor",
    date: new Date(),
    endDate: new Date(),
    tempStartDate: "",
    tempStartTime: "",
    tempEndDate: "",
    tempEndTime: "",
    description: "",
    clientId: "",
    color: "",
  },
  userIds: [],
};

export default {
  components: {
    AlertMessage,
    ClientSelect,
    DatePicker,
    UserSelect,
    TimePicker,
    ColorSelect,
    TaskCombo,
    AttachedFileListDialog,
    TaskForm,
  },
  props: {
    item: Object,
    show: [Boolean, String],
    title: String,
  },
  mixins: [validationMixin, formatterMixin],
  validations: {
    form: {
      userIds: { required },
      task: {
        name: {},
        status: {},
        code: {},
        tempStartDate: {},
        tempStartTime: {},
        tempEndDate: {},
        tempEndTime: {},
        clientId: { required },
        description: {},
      },
    },
  },

  data() {
    return {
      oldUsers: [],
      showDialog: this.show,
      src: null,
      jobDetail: {},
      form: {
        ...defaultInputs,
        task: {
          ...defaultInputs.task,
          clientId: this.clientId,
        },
        materials: [],
      },
      dialog: false,
      valid: false,
      loading: false,
      loadingDelete: false,
      showAttachFile: false,
      showJobDetail: false,
      materials: [],
    };
  },

  watch: {
    show(n, o) {
      if (n && !o) {
        this.dialog = true;
      }
    },

    src(n) {
      if (n) {
        this.form.task.name = typeof n == "string" ? n : n.name;
        if (n && n.name) {
          this.form.task.id = n.id;
          this.form.task.type = n.type;
          this.form.task.clientId = n.clientId;
          this.form.task.description = n.description;
        }
      }
    },

    dialog(n, o) {
      if (!n && o) {
        this.onDialogClose();
      } else {
        this.onDialogOpen();
      }
    },

    item(newTask, oldTask) {
      if (
        newTask &&
        newTask.id &&
        JSON.stringify(newTask) !== JSON.stringify(oldTask)
      ) {
        this.processInputRaw(newTask);
      } else {
        this.processInputRaw({
          ...newTask,
          date: new Date(),
          endDate: new Date(),
        });
      }
    },
  },

  computed: {
    ...mapState({
      account: (state) => state.account,
      user: (state) => state.account.data.user,
      task: (state) => state.task,
      alert: (state) => state.alert,
    }),
    formTitle() {
      let stuff = this.title ? this.title : "Job";
      return this.isEdit ? `${stuff}` : `${stuff}`;
    },
    isEdit() {
      return UTIL.gv(this.item, "id", "") ? true : false;
    },
    taskId() {
      return UTIL.gv(this.item, "id", "");
    },
    isDatetimeDisabled() {
      return false;
      // return this.isEdit ? true : false;
    },
  },

  methods: {
    ...mapActions("task", ["create", "update"]),
    ...mapActions({
      createTimeClock: "timeclock/createFromTask",
      deleteTask: "task/delete",
    }),

    processInputRaw(taskObj) {
      let userIds = [];
      if (taskObj.taskUsers) {
        userIds = taskObj.taskUsers.map((user) => user.userId);
      }
      this.oldUsers = userIds;

      if (!taskObj.date) {
        taskObj.date = new Date;
      }

      if (!taskObj.endDate) {
        taskObj.endDate = new Date;
      }

      let tempStartTime = 0;
      if (taskObj.start) {
        tempStartTime = this.timestampToTime(taskObj.start);
      } else if (taskObj.date) {
        tempStartTime = this.momentLocal(taskObj.date).format("HH:mm");
      } else {
        tempStartTime = this.momentLocal().format("HH:mm");
      }

      let tempEndTime = 0;
      if (taskObj.end) {
        tempEndTime = this.timestampToTime(taskObj.end);
      } else if (taskObj.endDate) {
        tempEndTime = this.momentLocal(taskObj.endDate).format("HH:mm");
      } else {
        tempEndTime = this.momentLocal().format("HH:mm");
      }      

      this.form = {
        task: {
          ...taskObj,
          tempStartDate: this.momentLocal(taskObj.date).format("YYYY-MM-DD"),
          tempStartTime: tempStartTime,
          tempEndDate: this.momentLocal(taskObj.endDate).format("YYYY-MM-DD"),
          tempEndTime: tempEndTime,
        },
        materials: taskObj.jobMaterials,
        userIds,
      };
      this.materials = taskObj.jobMaterials || [];
      this.src = taskObj.name;
    },

    handleChangeName(newEntry) {
      if (typeof newEntry === "string") {
        alert("Please select existing Job Name!");
      } else if (newEntry && newEntry.name) {
        this.processInputRaw(newEntry);
      }
    },

    viewOrAddFile() {
      this.showAttachFile = true;
    },

    viewJobDetail() {
      this.showJobDetail = true;
      this.jobDetail = this.item;
    },

    clearJobDetail() {
      this.showJobDetail = false;
    },

    clearAttachFile() {
      this.showAttachFile = false;
    },

    refreshAndClose() {
      this.$emit("onSucceded");
      this.dialog = false;
    },

    async deleteSchedule() {
      this.loadingDelete = true;
      let payload = {
        id: this.item.id,
        task: { ...this.item, isScheduled: false },
      };
      this.$emit("onScheduleDeleted", this.item);
      await this.update(payload);
      this.loadingDelete = false;
      this.dialog = false;
    },

    initForm() {
      if (this.clientId) {
        let categories = this.optional(this.item, "categories", []) || [];
        this.form = {
          ...this.form,
          task: {
            ...this.form.task,
            clientId: this.clientId,
            time: "00:00:00",
            tags: categories.map((category) => category.id),
            color: "#0a8042",
          },
        };
      }
    },

    onDialogClose() {
      if (this.isEdit) {
        //
      } else {
        //
      }
      this.$emit("onClosed");
    },

    onDialogOpen() {
      this.$store.dispatch("alert/clear");
      this.initForm();
    },
    addNew() {
      this.dialog = true;
      this.form = {
        ...defaultInputs,
      };
    },
    completed() {
      this.$emit("onSucceded");
    },
    async submit() {
      this.loading = true;
      if (!this.valid) {
        this.$store.dispatch("snackbar/error", "Form invalid");
        return false;
      }

      let oldUsers = this.oldUsers;

      const { tempStartDate, tempStartTime, tempEndDate, tempEndTime } = this.form.task;

      let formattedStartTime = tempStartTime;
      let formattedEndTime = tempEndTime;

      let fDateTimeStart = `${tempStartDate} ${formattedStartTime}`;
      let fDateTimeEnd = `${tempEndDate} ${formattedEndTime}`;

      let utcDateTimeStart = this.localToUtc(fDateTimeStart);
      let utcDateTimeEnd = this.localToUtc(fDateTimeEnd);

      let result = await userService.getAllAvailable({
        dateStart: fDateTimeStart,
        dateEnd: fDateTimeEnd,
      });

      let original = result.data;
      let availableUsers = [...original.map((user) => user.id), ...oldUsers];

      let notAllowed = this.form.userIds.filter(
        (x) => !availableUsers.includes(x)
      );

      if (
        notAllowed.length > 0 &&
        !confirm(
          "Some users already assigned to other schedule. \nAre you sure will continue?"
        )
      ) {
        this.loading = false;
        return false;
      }

      let payload = {
        ...this.form,
        task: {
          ...this.form.task,
          date: utcDateTimeStart,
          endDate: utcDateTimeEnd,
          isScheduled: true,
        },
        tags: this.form.task.tags,
        materials: this.materials.map((item) => {
          delete item.id;
          return {
            ...item,
          };
        }),
      };

      if (
        payload.task.status === "open" ||
        payload.task.status === "inprogress"
      ) {
        payload.task.isCompleted = false;
        if (!payload.task.invoiceId) {
          payload.task.isInvoiced = false;
        }
      } else if (payload.task.status === "completed") {
        payload.task.isCompleted = true;
      } else if (payload.task.status === "invoiced") {
        payload.task.isCompleted = true;
        if (payload.task.invoiceId) {
          payload.task.isInvoiced = true;
        }
      } else if (payload.task.status === "canceled") {
        payload.task.isCompleted = false;
        if (!payload.task.invoiceId) {
          payload.task.isInvoiced = false;
        }
      }

      if (this.isEdit) {
        await this.update({
          id: this.taskId,          
          ...payload,
        });
      } else {
        await this.update({
          id: payload.task.id,
          ...payload,
        });
        if (!this.task.status.error) {
          // await this.createTimeClock({
          //   id: this.task.single.id,
          //   ...payload,
          // });
        }
      }

      if (!this.task.status.error) {
        this.dialog = false;
        this.completed();
      }
      this.loading = false;
    },
  },
};
</script>